import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-surface-hover [a&]:hover:bg-surface-hover/90",
        "default-outline":
          "border-text-secondary bg-text-secondary/10 text-text-secondary [a&]:hover:bg-text-secondary/40",
        accent:
          "border-transparent bg-accent text-accent-foreground [a&]:hover:bg-accent/90",
        "accent-outline":
          "border-accent bg-accent/10 text-accent [a&]:hover:bg-accent/40",
        success:
          "border-transparent bg-success text-accent-foreground [a&]:hover:bg-success/90",
        "success-outline":
          "border-success bg-success/10 text-success [a&]:hover:bg-success/40",
        warning:
          "border-transparent bg-warning text-accent-foreground [a&]:hover:bg-warning/90",
        "warning-outline":
          "border-warning bg-warning/10 text-warning [a&]:hover:bg-warning/40",
        destructive:
          "border-transparent bg-destructive text-accent-foreground [a&]:hover:bg-destructive/90",
        "destructive-outline":
          "border-destructive bg-destructive/10 text-destructive [a&]:hover:bg-destructive/40",
        info: "border-transparent bg-info text-accent-foreground [a&]:hover:bg-info/90",
        "info-outline":
          "border-info bg-info/10 text-info [a&]:hover:bg-info/40",
        purple:
          "border-transparent bg-purple text-accent-foreground [a&]:hover:bg-purple/90",
        "purple-outline":
          "border-purple bg-purple/10 text-purple [a&]:hover:bg-purple/40",
        base: "border-transparent bg-surface-base text-text-secondary [a&]:hover:bg-surface-canvas",
        "base-outline":
          "border-subtle bg-surface-base text-text-secondary [a&]:hover:bg-surface-canvas",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Badge({
  className,
  variant,
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: "span",
    render,
    props: mergeProps<"span">(
      {
        "data-slot": "badge",
        className: cn(badgeVariants({ variant }), className),
      } as React.ComponentProps<"span">,
      props,
    ),
  });
}

export { Badge, badgeVariants };
